import { useEffect, useState } from "react";
import { state } from "../state/workspaceStore.js";
import { collectionName, stockStatus, save } from "../utils/domain/model.js";
import { head } from "../components/common/PageHeader.jsx";
import { btn } from "../components/forms/FormControls.jsx";
import { searchbar } from "../components/common/SearchToolbar.jsx";
import { nodes, lineBreaks } from "../components/common/reactHelpers.jsx";
import { image } from "../components/products/ProductImage.jsx";
import { esc, size, money } from "../utils/format.js";
import { badge } from "../components/common/StatusBadge.jsx";
import { tableViews } from "../components/tables/RecordTable.jsx";

/* =========================================================
   API
========================================================= */

const PRODUCT_API = "https://api.praachinorganics.com/api/products";

const COLLECTION_API = "https://api.praachinorganics.com/api/Collections";
const API_BASE_URL = "https://api.praachinorganics.com";

/* =========================================================

   PRODUCT STATUS

\========================================================= */

function getProductImageUrl(imageUrl) {
  if (!imageUrl) return "";

  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl;
  }

  return `${API_BASE_URL.replace(/\/$/, "")}/${imageUrl.replace(/^\//, "")}`;
}

/* =========================================================
   ACTION ICON
========================================================= */

export function actionIcon(type) {
  const paths = {
    view: (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z"></path>

        <circle cx="12" cy="12" r="3"></circle>
      </>
    ),

    edit: <path d="m16 3 5 5-12 12-6 1 1-6Z M14 5l5 5"></path>,

    delete: (
      <path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7"></path>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[type]}
    </svg>
  );
}

/* =========================================================
   PRODUCT STATUS
========================================================= */

function statusFromApi(value) {
  if (value === "Draft" || value === "Active") {
    return value;
  }

  if (value === "ComingSoon" || value === "Coming Soon") {
    return "Coming Soon";
  }

  if (value === "OutOfStock" || value === "Out of Stock") {
    return "Out of Stock";
  }

  return (
    {
      1: "Draft",
      2: "Active",
      3: "Coming Soon",
      4: "Out of Stock",
    }[Number(value)] || "Draft"
  );
}

/* =========================================================
   UNIT
========================================================= */

function unitFromApi(value) {
  if (value === "Gram") {
    return "g";
  }

  if (value === "Kilogram") {
    return "kg";
  }

  if (value === "Millilitre") {
    return "ml";
  }

  if (value === "Litre") {
    return "l";
  }

  return (
    {
      1: "g",
      2: "kg",
      3: "ml",
      4: "l",
    }[Number(value)] || "g"
  );
}

/* =========================================================
   COLLECTION MAP
========================================================= */

function mapCollection(c) {
  return {
    id: String(c.id),

    name: c.name || "",

    code: c.code || "",

    description: c.description || "",

    order: Number(c.displayOrder ?? c.order ?? 1),

    launch:
      c.status === "Active" || Number(c.status) === 1 || c.launch === true,

    archived: false,
  };
}

/* =========================================================
   PRODUCT MAP
========================================================= */

function mapProduct(item, collections) {
  const v = item?.variant || {};

  const collection = collections.find(
    (c) =>
      String(c.name).trim().toLowerCase() ===
      String(item?.collectionName || "")
        .trim()
        .toLowerCase(),
  );

  return {
    id: String(item?.id ?? ""),

    name: item?.name || "",

    collection:
      item?.collectionId != null
        ? String(item.collectionId)
        : String(collection?.id || ""),

    collectionName: item?.collectionName || "",

    variant: v.variantName || "",

    status: statusFromApi(item?.status),

    description: item?.description || "",

    photos: item?.image?.imageUrl ? [item.image.imageUrl] : [],

    skus: [
      {
        id: String(v.id ?? ""),

        code: v.skuCode || "",

        variant: v.variantName || "",

        qty: v.netQuantity ?? "",

        unit: unitFromApi(v.unit),

        mrp: v.mrp ?? 0,

        discount: v.discountPercentage ?? 0,

        price: v.sellingPrice ?? 0,

        stock: v.stock ?? 0,

        threshold: v.lowStockThreshold ?? 0,

        weight: v.packedWeightGrams ?? 0,

        dimensions: [v.lengthCm, v.widthCm, v.heightCm]
          .filter((x) => x != null)
          .join(" × "),
      },
    ],

    ingredients: item?.ingredients || "",

    nutrition: item?.nutrition || "",

    allergens: item?.allergenDeclaration || "",

    shelfLife: item?.shelfLife || "",

    storage: item?.storageInstructions || "",

    manufacturer: [item?.manufacturerName, item?.manufacturerAddress]
      .filter(Boolean)
      .join("\n"),

    marketer: [item?.marketerName, item?.marketerAddress]
      .filter(Boolean)
      .join("\n"),

    fssai: item?.fssaiLicenseDetails || "",

    infoMode: item?.informationProvenance || "reference",

    taxRate: item?.gstRate ?? null,

    complianceVerified: Boolean(item?.labelComplianceReviewed),

    archived: false,
  };
}

/* =========================================================
   POPUP
========================================================= */

function ProductPopup({ popup, onClose, onConfirm, deleting }) {
  if (!popup) {
    return null;
  }

  const isConfirm = popup.type === "confirm";

  const isSuccess = popup.type === "success";

  const isError = popup.type === "error";

  return (
    <div className="product-popup-backdrop" role="presentation">
      <div
        className="product-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-popup-title"
      >
        <div
          className={`product-popup-icon ${isSuccess ? "success" : isError ? "error" : "warning"
            }`}
        >
          {isSuccess ? "✓" : isError ? "!" : "?"}
        </div>

        <h2 id="product-popup-title">{popup.title}</h2>

        <p>{popup.message}</p>

        <div className="product-popup-actions">
          {isConfirm ? (
            <>
              <button
                type="button"
                className="btn secondary"
                disabled={deleting}
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn"
                disabled={deleting}
                onClick={onConfirm}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </>
          ) : (
            <button type="button" className="btn" onClick={onClose}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PRODUCTS TABLE
========================================================= */

export function products(onDeleteProduct, deletingId) {
  const rows = state.db.products.filter(
    (p) =>
      (state.filter === "Archived" ? p.archived : !p.archived) &&
      `${p.name} ${collectionName(state.db, p.collection)} ${p.skus
        .map((s) => s.code)
        .join(" ")}`
        .toLowerCase()
        .includes(state.query.toLowerCase()) &&
      (state.filter === "All" ||
        state.filter === "Archived" ||
        (state.filter === "Low stock" &&
          p.skus.some((s) => s.stock <= s.threshold)) ||
        p.status === state.filter),
  );

  return (
    <>
      {head(
        "CATALOGUE",

        "Products",

        "Manage product information, SKU pricing and stock.",

        btn("＋ Add product", 'data-nav="product-edit"'),
      )}

      <div className="card">
        {searchbar(
          "Search products, collections, SKUs…",

          [
            "All",
            "Active",
            "Draft",
            "Coming Soon",
            "Out of Stock",
            "Low stock",
            "Archived",
          ],
        )}

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>

                <th>Collection / SKU</th>

                <th>Selling / MRP</th>

                <th>Stock</th>

                <th>Status</th>

                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {rows.length > 0 ? (
                nodes(
                  rows.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="product-cell">
                          <img
                            src={getProductImageUrl(p.photos?.[0])}
                            alt={`${p.name} product image`}
                            style={{
                              width: "180px",
                              height: "180px",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />

                          <div>
                            <b>{esc(p.name)}</b>
                            <small>{p.skus.map(size).join(" · ")}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <b>{esc(collectionName(state.db, p.collection))}</b>

                        <small>
                          {esc(p.skus.map((s) => s.code).join(" · "))}
                        </small>
                      </td>

                      <td>
                        {lineBreaks(
                          p.skus.map(
                            (s) => money(s.price) + " / " + money(s.mrp),
                          ),
                        )}
                      </td>

                      <td>
                        {p.skus.reduce(
                          (n, s) => n + s.stock,

                          0,
                        )}

                        <small>{badge(stockStatus(p))}</small>
                      </td>

                      <td>{badge(p.archived ? "Archived" : p.status)}</td>

                      <td>
                        <div className="action-buttons">
                          {/* VIEW */}

                          <button
                            type="button"
                            className="table-action"
                            data-nav="product"
                            data-id={p.id}
                            title="View product"
                            aria-label="View product"
                          >
                            {actionIcon("view")}
                          </button>

                          {/* EDIT */}

                          <button
                            type="button"
                            className="table-action"
                            data-nav="product-edit"
                            data-id={p.id}
                            title="Edit product"
                            aria-label="Edit product"
                          >
                            {actionIcon("edit")}
                          </button>

                          {/* DELETE */}

                          <button
                            type="button"
                            className="table-action"
                            title="Delete product"
                            aria-label="Delete product"
                            disabled={String(deletingId) === String(p.id)}
                            onClick={() => onDeleteProduct(p)}
                          >
                            {actionIcon("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )),
                )
              ) : (
                <tr>
                  <td colSpan="6" className="empty">
                    No matching products.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* =========================================================
   PRODUCTS PAGE
========================================================= */

export default function ProductsPage() {
  const [, setRevision] = useState(0);

  const [deletingId, setDeletingId] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);

  const [popup, setPopup] = useState(null);

  /* =======================================================
     GET PRODUCTS + COLLECTIONS
  ======================================================= */

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        console.log("PRODUCTS PAGE GET START");

        const [productResponse, collectionResponse] = await Promise.all([
          fetch(PRODUCT_API, {
            headers: {
              Accept: "application/json",
            },
          }),

          fetch(COLLECTION_API, {
            headers: {
              Accept: "application/json",
            },
          }),
        ]);

        if (!productResponse.ok) {
          throw new Error(
            `Unable to load products (${productResponse.status}).`,
          );
        }

        if (!collectionResponse.ok) {
          throw new Error(
            `Unable to load collections (${collectionResponse.status}).`,
          );
        }

        const apiProducts = await productResponse.json();

        const apiCollections = await collectionResponse.json();

        const collections = (
          Array.isArray(apiCollections) ? apiCollections : []
        ).map(mapCollection);

        const mappedProducts = (
          Array.isArray(apiProducts) ? apiProducts : []
        ).map((p) => mapProduct(p, collections));

        if (!alive) {
          return;
        }

        state.db.collections = collections;

        state.db.products = mappedProducts;

        save(localStorage, state.db);

        console.log("PRODUCTS PAGE GET SUCCESS:", mappedProducts);

        setRevision((n) => n + 1);
      } catch (error) {
        console.error("PRODUCTS PAGE GET ERROR:", error);

        if (alive) {
          setPopup({
            type: "error",

            title: "Unable to load products",

            message:
              error?.message || "Something went wrong while loading products.",
          });
        }
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  /* =======================================================
     OPEN DELETE POPUP
  ======================================================= */

  function handleDeleteProduct(product) {
    const productId = Number(product?.id);

    if (!Number.isInteger(productId) || productId <= 0) {
      setPopup({
        type: "error",

        title: "Invalid product",

        message: "A valid product ID is required.",
      });

      return;
    }

    setPendingDelete(product);

    setPopup({
      type: "confirm",

      title: "Delete product?",

      message: `Are you sure you want to delete "${product.name}"? This product will be removed from the catalogue.`,
    });
  }

  /* =======================================================
     CONFIRM DELETE
  ======================================================= */

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }

    const productId = Number(pendingDelete.id);

    try {
      setDeletingId(productId);

      const response = await fetch(`${PRODUCT_API}/${productId}`, {
        method: "DELETE",

        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        let message = "Unable to delete the product.";

        try {
          const data = await response.json();

          message = data?.message || data?.title || message;
        } catch {
          // no JSON body
        }

        throw new Error(message);
      }

      state.db.products = state.db.products.filter(
        (item) => String(item.id) !== String(productId),
      );

      save(localStorage, state.db);

      setRevision((n) => n + 1);

      const deletedName = pendingDelete.name;

      setPendingDelete(null);

      setPopup({
        type: "success",

        title: "Product deleted",

        message: `"${deletedName}" was deleted successfully.`,
      });
    } catch (error) {
      console.error("PRODUCT DELETE ERROR:", error);

      setPopup({
        type: "error",

        title: "Delete failed",

        message: error?.message || "Unable to delete the product.",
      });
    } finally {
      setDeletingId(null);
    }
  }

  /* =======================================================
     CLOSE POPUP
  ======================================================= */

  function closePopup() {
    if (deletingId) {
      return;
    }

    setPopup(null);

    setPendingDelete(null);
  }

  return (
    <>
      {/* ===================================================
          INLINE CSS FOR ACTIONS + RESPONSIVE POPUP
      =================================================== */}

      <style>
        {`
          .action-buttons{
            display:flex;
            align-items:center;
            gap:6px;
            flex-wrap:nowrap;
          }

          .action-buttons .table-action{
            flex:none;
          }

          .product-popup-backdrop{
            position:fixed;
            inset:0;
            z-index:9999;
            display:flex;
            align-items:center;
            justify-content:center;
            padding:20px;
            background:#183e2c80;
          }

          .product-popup{
            width:min(460px,100%);
            max-height:calc(100vh - 40px);
            overflow:auto;
            background:#fff;
            border:1px solid #d8e5d6;
            border-radius:14px;
            box-shadow:0 30px 100px #122e2b50;
            padding:28px;
            text-align:center;
            color:#2d5138;
          }

          .product-popup-icon{
            width:52px;
            height:52px;
            margin:0 auto 18px;
            border-radius:50%;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:24px;
            font-weight:800;
          }

          .product-popup-icon.warning{
            background:#fff3dc;
            color:#976d2e;
          }

          .product-popup-icon.success{
            background:#e5f4e6;
            color:#347144;
          }

          .product-popup-icon.error{
            background:#fbeae7;
            color:#ab5143;
          }

          .product-popup h2{
            margin:0 0 10px;
          }

          .product-popup p{
            margin:0;
            color:#6d8872;
            font-size:13px;
            line-height:1.6;
            overflow-wrap:anywhere;
          }

          .product-popup-actions{
            display:flex;
            justify-content:flex-end;
            gap:10px;
            margin-top:24px;
          }

          @media(max-width:650px){
            .product-popup-backdrop{
              padding:14px;
              align-items:flex-end;
            }

            .product-popup{
              width:100%;
              padding:22px 18px;
              border-radius:14px;
            }

            .product-popup-actions{
              flex-direction:column-reverse;
            }

            .product-popup-actions .btn{
              width:100%;
            }

            .action-buttons{
              gap:4px;
            }
          }
        `}
      </style>

      {tableViews(products(handleDeleteProduct, deletingId))}

      <ProductPopup
        popup={popup}
        deleting={Boolean(deletingId)}
        onClose={closePopup}
        onConfirm={confirmDelete}
      />
    </>
  );
}
